import dayjs from 'dayjs';
import { BehaviorConfigOption } from 'src/graphql/entities/behavior/behavior.types';
import { CustomViewFormConditionCustomData } from 'src/graphql/entities/custom-view/custom-view.types';
import { ConditionInput, ConditionOperator } from 'src/graphql/types';

export const CPP_SERVICE_LEVEL_FILTER_VALUE = 'CPP';

const NON_MEMBER_FILTER_VALUE = 'non_member';

function mapTierConditions(
  data: CustomViewFormConditionCustomData,
  slaIdAccessor: string,
  cppExpDateAccessor: string,
): ConditionInput[] {
  const operator = data.operator as ConditionOperator;
  const values = String(data.value).split(',').filter(Boolean);
  const hasCpp = values.includes(CPP_SERVICE_LEVEL_FILTER_VALUE);
  const tiers = values.filter((value) => value && value !== CPP_SERVICE_LEVEL_FILTER_VALUE);
  const today = dayjs().format('YYYY-MM-DD');

  const tiersWithCpp = (
    cppOperator: ConditionOperator,
    combinator: NonNullable<ConditionInput['logicalOperator']>,
  ): ConditionInput[] => {
    const conditions: ConditionInput[] = tiers.length
      ? [{ ...data, attribute: slaIdAccessor, value: tiers.join(',') }]
      : [];

    conditions.push({
      attribute: cppExpDateAccessor,
      operator: cppOperator,
      value: today,
      logicalOperator: conditions.length ? combinator : data.logicalOperator,
    });

    return conditions;
  };

  if (hasCpp && operator === ConditionOperator.In) {
    return tiersWithCpp(ConditionOperator.GreaterThan, 'or');
  }

  if (hasCpp && operator === ConditionOperator.NotIn) {
    return tiersWithCpp(ConditionOperator.LessOrEqThanOrNull, 'and');
  }

  // non-In / plain In without CPP: bare SLA ids.
  if (operator !== ConditionOperator.In) {
    return [{ ...data, attribute: slaIdAccessor }];
  }

  // Plain tiers only: exclude active complimentary memberships (1V-5494).
  return [
    { ...data, attribute: slaIdAccessor },
    {
      attribute: cppExpDateAccessor,
      operator: ConditionOperator.LessOrEqThanOrNull,
      value: today,
      logicalOperator: 'and',
    },
  ];
}

export function mapServiceLevelFilter(
  data: CustomViewFormConditionCustomData,
  slaIdAccessor: string,
  cppExpDateAccessor: string,
): ConditionInput[] {
  const values = String(data.value).split(',').filter(Boolean);
  const tierValues = values.filter((value) => value !== NON_MEMBER_FILTER_VALUE);
  const wantsNonMember = tierValues.length !== values.length;

  const tierData = wantsNonMember ? { ...data, value: tierValues.join(',') } : data;
  const tierConditions = tierValues.length
    ? mapTierConditions(tierData, slaIdAccessor, cppExpDateAccessor)
    : [];

  if (!wantsNonMember) {
    return tierConditions;
  }

  const isIn = (data.operator as ConditionOperator) === ConditionOperator.In;

  let logicalOperator = data.logicalOperator;

  if (tierConditions.length) {
    logicalOperator = isIn ? 'or' : 'and';
  }

  return [
    ...tierConditions,
    {
      attribute: slaIdAccessor,
      operator: isIn ? ConditionOperator.Empty : ConditionOperator.NotEmpty,
      value: '',
      logicalOperator,
    },
  ];
}

export function buildServiceLevelFilterOptions(
  tiers: { platformSlaId: string | null; label: string }[],
): BehaviorConfigOption[] {
  const options = tiers.map((tier) => {
    const value = tier.platformSlaId ?? NON_MEMBER_FILTER_VALUE;
    return { id: value, value, label: tier.label };
  });

  if (tiers.some((tier) => tier.platformSlaId)) {
    options.push({
      id: CPP_SERVICE_LEVEL_FILTER_VALUE,
      value: CPP_SERVICE_LEVEL_FILTER_VALUE,
      label: CPP_SERVICE_LEVEL_FILTER_VALUE,
    });
  }

  return options.sort((a, b) => a.label.localeCompare(b.label));
}
